import { useMemo, type ChangeEvent } from 'react';
import { localizedCode } from '../data/positions.ts';
import { useSquad } from '../lib/squadContext.ts';
import {
  age,
  decimal,
  isForeign,
  isListBEligible,
  money,
  STATUS_CODES,
  STATUS_META,
  STATUS_ORDER,
  suitability,
  YOUNG_FOREIGN_BIRTH_YEAR,
} from '../lib/rules.ts';
import { C } from '../lib/theme.ts';
import type { Filters, Player, PositionCode, SortField, SquadStatus } from '../types.ts';

interface ColumnSpec {
  field: SortField;
  label: string;
  align: 'left' | 'right';
  padding: string;
}

/** The twelve sortable columns, in the order they are drawn. */
const COLUMNS: ColumnSpec[] = [
  { field: 'shirtNo', label: '#', align: 'left', padding: '8px 8px' },
  { field: 'name', label: 'OYUNCU', align: 'left', padding: '8px 8px' },
  { field: 'position', label: 'MEVKİ', align: 'left', padding: '8px 6px' },
  { field: 'age', label: 'YAŞ', align: 'right', padding: '8px 6px' },
  { field: 'marketValue', label: 'DEĞER', align: 'right', padding: '8px 6px' },
  { field: 'wage', label: 'MAAŞ', align: 'right', padding: '8px 6px' },
  { field: 'appearances', label: 'MAÇ', align: 'right', padding: '8px 6px' },
  { field: 'minutes', label: 'DK', align: 'right', padding: '8px 6px' },
  { field: 'goalsAssists', label: 'G/A', align: 'right', padding: '8px 6px' },
  { field: 'rating', label: 'REY.', align: 'right', padding: '8px 6px' },
  { field: 'form', label: 'FORM', align: 'left', padding: '8px 6px' },
  { field: 'status', label: 'STATÜ', align: 'left', padding: '8px 8px' },
];

/**
 * Sort key per column. Strings are compared with the Turkish collation; the MEVKİ
 * column sorts on the primary position's short label so the order reads alphabetically.
 */
const SORT_KEYS: Record<SortField, (p: Player) => string | number> = {
  shirtNo: (p) => p.shirtNo,
  name: (p) => p.name,
  position: (p) => localizedCode(p.positions[0]),
  age: (p) => age(p),
  marketValue: (p) => p.marketValue,
  wage: (p) => p.wage,
  appearances: (p) => p.appearances,
  minutes: (p) => p.minutes,
  goalsAssists: (p) => p.goals + p.assists,
  rating: (p) => p.rating,
  form: (p) => p.form.reduce((total, v) => total + v, 0) / p.form.length,
  status: (p) => STATUS_ORDER[p.status],
};

export default function SquadTable() {
  const { state, dispatch, players, playerHandles } = useSquad();
  const { filters, sortField, sortDirection } = state;

  const rows = useMemo(() => {
    let list = players.slice();

    if (filters.query) {
      const q = filters.query.toLocaleLowerCase('tr');
      list = list.filter(
        (p) =>
          p.name.toLocaleLowerCase('tr').indexOf(q) >= 0 ||
          p.nationality.toLowerCase().indexOf(q) >= 0,
      );
    }
    if (filters.position) {
      list = list.filter((p) => suitability(p, filters.position as PositionCode) > 0);
    }
    if (filters.age === 'u19') list = list.filter((p) => age(p) < 19);
    if (filters.age === 'u21') list = list.filter((p) => age(p) < 21);
    if (filters.age === 'u23') list = list.filter((p) => age(p) < 23);
    if (filters.age === '2003') list = list.filter((p) => p.birthYear >= YOUNG_FOREIGN_BIRTH_YEAR);
    if (filters.age === '30') list = list.filter((p) => age(p) >= 30);
    if (filters.nationality === 'yerli') list = list.filter((p) => p.turkeyEligible);
    if (filters.nationality === 'yabanci') list = list.filter((p) => !p.turkeyEligible);
    if (filters.homegrown === 'kulup') list = list.filter((p) => p.homegrown === 'kulup');
    if (filters.homegrown === 'hg') list = list.filter((p) => p.homegrown !== 'yok');
    if (filters.homegrown === 'listeb') list = list.filter(isListBEligible);

    // The three synthetic status filters are exclusive with the plain status codes.
    if (filters.status === 'yeni') list = list.filter((p) => p.isNew);
    else if (filters.status === 'plan')
      list = list.filter((p) => p.status === 'KC' || p.status === 'SC');
    else if (filters.status === 'cikis')
      list = list.filter((p) => p.status === 'KD' || p.status === 'SD');
    else if (filters.status) list = list.filter((p) => p.status === filters.status);

    const key = SORT_KEYS[sortField];
    list.sort((a, b) => {
      const x = key(a);
      const y = key(b);
      if (typeof x === 'string') return x.localeCompare(y as string, 'tr') * sortDirection;
      return ((x as number) - (y as number)) * sortDirection;
    });
    return list;
  }, [players, filters, sortField, sortDirection]);

  const changeFilter =
    (field: keyof Filters) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      dispatch({ type: 'setFilter', field, value: e.target.value });

  const changeStatus = (p: Player) => (e: ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const value = e.target.value;
    if (value === 'SIL') {
      dispatch({ type: 'removePlayer', id: p.id });
      return;
    }
    dispatch({ type: 'setStatus', id: p.id, status: value as SquadStatus });
  };

  return (
    <section
      style={{
        gridColumn: '2',
        order: 2,
        borderRight: '2px solid ' + C.ink,
        borderBottom: '2px solid ' + C.ink,
        background: C.bg,
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '11px 16px',
          borderBottom: '2px solid ' + C.ink,
          background: C.surface,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em' }}>
          {'KADRO · A TAKIM & PLANLAMA'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{ fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: '0.08em' }}
          >
            {rows.length + ' / ' + players.length + ' oyuncu'}
          </span>
          <button
            onClick={() => dispatch({ type: 'setTransferModal', open: true })}
            style={{
              border: '2px solid ' + C.red,
              background: C.red,
              color: C.bg,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '5px 11px',
              cursor: 'pointer',
            }}
          >
            + KADROYA EKLE
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 0,
          borderBottom: '2px solid ' + C.ink,
          overflow: 'hidden',
        }}
      >
        <input
          type="text"
          name="query"
          value={filters.query}
          onChange={changeFilter('query')}
          placeholder="Oyuncu ara…"
          style={{
            flex: '2 1 130px',
            minWidth: 110,
            border: 0,
            borderRight: '1px solid ' + C.line,
            background: C.bg,
            padding: '9px 10px',
            fontSize: 11.5,
            fontWeight: 600,
          }}
        />
        <select
          name="position"
          value={filters.position}
          onChange={changeFilter('position')}
          style={{
            flex: '0 1 auto',
            minWidth: 0,
            border: 0,
            borderRight: '1px solid ' + C.line,
            background: C.bg,
            padding: '9px 6px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <option value="">TÜM MEVKİLER</option>
          <option value="GK">Kaleci</option>
          <option value="RB">Sağ Bek</option>
          <option value="RCB">Sağ Stoper</option>
          <option value="LCB">Sol Stoper</option>
          <option value="LB">Sol Bek</option>
          <option value="DM">Ön Libero</option>
          <option value="CM">Merkez Orta Saha</option>
          <option value="AM">Ofansif Orta Saha</option>
          <option value="RW">Sağ Kanat</option>
          <option value="LW">Sol Kanat</option>
          <option value="ST">Santrfor</option>
        </select>
        <select
          name="age"
          value={filters.age}
          onChange={changeFilter('age')}
          style={{
            flex: '0 1 auto',
            minWidth: 0,
            border: 0,
            borderRight: '1px solid ' + C.line,
            background: C.bg,
            padding: '9px 6px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <option value="">TÜM YAŞLAR</option>
          <option value="u19">U19</option>
          <option value="u21">U21</option>
          <option value="u23">U23</option>
          <option value="2003">2003+ doğumlu</option>
          <option value="30">30 yaş üstü</option>
        </select>
        <select
          name="nationality"
          value={filters.nationality}
          onChange={changeFilter('nationality')}
          style={{
            flex: '0 1 auto',
            minWidth: 0,
            border: 0,
            borderRight: '1px solid ' + C.line,
            background: C.bg,
            padding: '9px 6px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <option value="">YERLİ + YABANCI</option>
          <option value="yerli">Yerli (A Milli uygun)</option>
          <option value="yabanci">Yabancı</option>
        </select>
        <select
          name="homegrown"
          value={filters.homegrown}
          onChange={changeFilter('homegrown')}
          style={{
            flex: '0 1 auto',
            minWidth: 0,
            border: 0,
            borderRight: '1px solid ' + C.line,
            background: C.bg,
            padding: '9px 6px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <option value="">TÜM YETİŞME</option>
          <option value="kulup">Kulüp altyapısı</option>
          <option value="hg">Yetiştirilmiş (HG)</option>
          <option value="listeb">Liste B uygun</option>
        </select>
        <select
          name="status"
          value={filters.status}
          onChange={changeFilter('status')}
          style={{
            flex: '0 1 auto',
            minWidth: 0,
            border: 0,
            borderRight: '1px solid ' + C.line,
            background: C.bg,
            padding: '9px 6px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <option value="">TÜM STATÜLER</option>
          <option value="A">A Listesinde</option>
          <option value="B">B Listesinde</option>
          <option value="D">Liste Dışında</option>
          <option value="plan">Planlı çıkış</option>
          <option value="cikis">Ayrılanlar</option>
          <option value="yeni">Yeni eklenenler</option>
        </select>
        <button
          onClick={() => dispatch({ type: 'resetFilters' })}
          title="Filtreleri temizle"
          style={{
            flex: '0 0 auto',
            border: 0,
            borderLeft: '1px solid ' + C.line,
            background: C.surface,
            color: C.ink,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.06em',
            padding: '9px 10px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          TEMİZLE
        </button>
      </div>

      <div className="sc" style={{ maxHeight: 620, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr
              style={{
                background: C.ink,
                color: C.bg,
                position: 'sticky',
                top: 0,
                zIndex: 2,
              }}
            >
              {COLUMNS.map((col) => {
                const active = sortField === col.field;
                return (
                  <th
                    key={col.field}
                    onClick={() => dispatch({ type: 'sortBy', field: col.field })}
                    style={{
                      textAlign: col.align,
                      padding: col.padding,
                      fontSize: 9.5,
                      letterSpacing: '0.1em',
                      fontWeight: 800,
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                      color: active ? C.gold : C.bg,
                      borderBottom: '2px solid ' + (active ? C.red : 'transparent'),
                    }}
                  >
                    {col.label + (active ? (sortDirection === 1 ? ' ▲' : ' ▼') : '')}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const meta = STATUS_META[p.status];
              const listBEligible = isListBEligible(p);
              const y = age(p);
              return (
                <tr
                  key={p.id}
                  {...playerHandles(p.id)}
                  title="Sürükleyip sahadaki mevkiye bırak"
                  style={{
                    borderBottom: '1px solid #dcd9d7',
                    background:
                      state.selectedId === p.id ? C.tint : p.isNew ? '#efe6df' : C.bg,
                    cursor: 'grab',
                    opacity: p.availability === 'X' ? 0.45 : 1,
                  }}
                >
                  <td
                    style={{
                      padding: '7px 8px',
                      fontWeight: 800,
                      color: C.muted,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {p.shirtNo}
                  </td>
                  <td style={{ padding: '7px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 3,
                          height: 18,
                          background: isForeign(p) ? C.red : C.ink,
                        }}
                      />
                      <span style={{ fontWeight: 800, letterSpacing: '-0.01em' }}>{p.name}</span>
                      <span
                        style={{
                          fontSize: 9.5,
                          fontWeight: 800,
                          letterSpacing: '0.06em',
                          color: C.muted,
                        }}
                      >
                        {p.nationality}
                      </span>
                      {p.isNew && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            letterSpacing: '0.1em',
                            background: C.red,
                            color: C.bg,
                            padding: '2px 5px',
                          }}
                        >
                          YENİ
                        </span>
                      )}
                      {isForeign(p) && p.birthYear >= YOUNG_FOREIGN_BIRTH_YEAR && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            letterSpacing: '0.1em',
                            border: '1px solid ' + C.red,
                            color: C.deep,
                            padding: '1px 4px',
                          }}
                        >
                          +4
                        </span>
                      )}
                      {p.homegrown !== 'yok' && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            letterSpacing: '0.1em',
                            background: C.ink,
                            color: C.bg,
                            padding: '2px 5px',
                          }}
                        >
                          {p.homegrown === 'kulup' ? 'KA' : 'HG'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    style={{ padding: '7px 6px', fontWeight: 700, fontSize: 11, color: C.ink2 }}
                  >
                    {p.positions.map(localizedCode).join(' · ')}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: y >= 31 ? C.deep : y <= 21 ? C.ink : C.ink2,
                    }}
                  >
                    {y}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {money(p.marketValue)}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: C.ink2,
                    }}
                  >
                    {money(p.wage)}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: C.ink2,
                    }}
                  >
                    {p.appearances}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: C.ink2,
                    }}
                  >
                    {p.minutes.toLocaleString('tr-TR')}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: C.ink2,
                    }}
                  >
                    {p.goals + '/' + p.assists}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums',
                      color: p.rating >= 7.2 ? C.deep : C.ink,
                    }}
                  >
                    {decimal(p.rating, 2)}
                  </td>
                  <td style={{ padding: '7px 6px' }}>
                    <div style={{ display: 'flex', gap: 2 }}>
                      {p.form.map((v, i) => (
                        <span
                          key={i}
                          style={{
                            width: 7,
                            height: 14,
                            background: v >= 7.2 ? C.ink : v >= 6.7 ? '#8a8583' : C.line,
                          }}
                        />
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: '5px 8px' }}>
                    <select
                      value={p.status}
                      onChange={changeStatus(p)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '3px 5px',
                        background: meta.bg,
                        color: meta.fg,
                        border: '1px solid ' + meta.border,
                        cursor: 'pointer',
                      }}
                    >
                      {STATUS_CODES.map((code) => (
                        <option
                          key={code}
                          value={code}
                          disabled={code === 'B' && !listBEligible}
                        >
                          {STATUS_META[code].label}
                        </option>
                      ))}
                      {p.isNew && <option value="SIL">Kaldır</option>}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div
            style={{
              padding: '36px 16px',
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 600,
              color: C.muted,
            }}
          >
            Bu filtrelerle eşleşen oyuncu yok.
          </div>
        )}
      </div>
    </section>
  );
}
